import { ProfileMapper } from '../../../common/mappers/ProfileMapper';
import { CallLimiting } from '../../../common/reports/models/CallLimiting';
import { Coverage } from '../../../common/reports/models/Coverage';
import { DateUtils } from '../../../common/utils/DateUtils';
import { GQLIdEncoder } from '../../../common/utils/GQLIdEncoder';
import { Report, ReportDone, ReportFailed, ReportPending } from '../models/Report';
import { Project } from '../models/valueObjects/Project';
import { ReportVersion } from '../models/valueObjects/ReportVersion';
import { ReportVersionMapper } from './ReportVersionMapper';
import { SlotMapper } from './SlotMapper';
import { TraceMapper } from './TraceMapper';

export class ReportMapper {
  readonly gqlIdEncoder = new GQLIdEncoder();
  readonly traceMapper = new TraceMapper();
  readonly profileMapper = new ProfileMapper();
  readonly slotMapper = new SlotMapper();
  readonly reportVersionMapper = new ReportVersionMapper();

  mapGQLModel(data: any): Report {
    const id = this.gqlIdEncoder.decode(data.id);
    const trace = data.trace ? this.traceMapper.mapGQLModel(data.trace) : null;
    const profile = data.profile ? this.profileMapper.mapGQLModel(data.profile) : null;
    const project = data.slot.project ? new Project(this.gqlIdEncoder.decode(data.slot.project.id), data.slot.project.name) : null;
    const slot = data.slot ? this.slotMapper.mapGQLModel(data.slot) : null;
    const coverage = data.coverageSummary ? new Coverage(data.coverageSummary.total, data.coverageSummary.covered, data.coverageSummary.depth ?? null) : null;
    const callLimiting = data.callLimiting
      ? new CallLimiting(
          data.callLimiting.callsLimited,
          data.callLimiting.maxCallsPerBucket ?? null,
          data.callLimiting.maxCallsTotal ?? null,
          data.callLimiting.numCallsKept ?? null,
          data.callLimiting.numCallsOverBucketLimit ?? null,
          data.callLimiting.numCallsOverTotalLimit ?? null,
          data.callLimiting.numCallsTotal ?? null,
          data.callLimiting.numBuckets ?? null,
          data.callLimiting.numFullBuckets ?? null
        )
      : null;

    // Map report versions sorted by most to least recent
    const versions: ReportVersion[] = data.slot.reports?.edges?.map((e: any) => this.reportVersionMapper.mapGQLModel(e.node)) || [];
    versions.sort((v1, v2) => v2.getDate().diff(v1.getDate()));

    if (data.__typename === 'ReportFailed') {
      return new ReportFailed(
        id,
        data.name,
        DateUtils.toMoment(data.createdAt),
        data.traceType,
        trace,
        profile,
        project,
        slot,
        data.ruleset,
        versions,
        data.reason
      );
    }
    if (data.__typename === 'ReportPending') {
      return new ReportPending(id, data.name, DateUtils.toMoment(data.createdAt), data.traceType, trace, profile, project, slot, data.ruleset, versions);
    }
    return new ReportDone(
      id,
      data.name,
      DateUtils.toMoment(data.createdAt),
      data.traceType,
      trace,
      profile,
      project,
      slot,
      data.ruleset,
      versions,
      DateUtils.toMoment(data.completedAt),
      data.nonsensitiveSalt,
      coverage,
      callLimiting,
      data?.rules?.totalCount ?? 0,
      data?.keys?.totalCount ?? 0,
      data?.certificates?.totalCount ?? 0,
      data?.handshakes?.totalCount ?? 0,
      data?.sshHandshakes?.totalCount ?? 0,
      data?.ciphersuites?.length ?? 0,
      data?.activeExceptions?.totalCount ?? 0, // TODO(QCST-221): Read project active exceptions for the report
      data?.operations?.totalCount ?? 0
    );
  }
}
